import { ChevronRight, StickyNote } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { READINESS_BADGE_META_BY_STATUS } from '../../constants';
import { Badge } from '../Badge';
import { BackBar, BriefRow } from '../Visit';
import HuddleNotes, { formatNoteStamp, NOTE_SOURCE_LABEL } from './HuddleNotes';
import styles from './HuddleNotes.module.scss';

const SUMMARY_PREVIEW_COUNT = 2;

export const HuddleNotesSummary = ({ notes = [], onOpen, heading = true }) => {
  const newest = [...notes].sort((a, b) => `${b.createdAt}`.localeCompare(`${a.createdAt}`));
  const shown = newest.slice(0, SUMMARY_PREVIEW_COUNT);
  const more = newest.length - shown.length;

  return (
    <button type="button" className={styles.summarySection} onClick={onOpen}>
      {heading && (
        <div className={styles.summaryHeader}>
          <span className={styles.summaryLabel}>
            <StickyNote size={12} aria-hidden="true" />
            Huddle notes
          </span>
          <span className={styles.summaryCount}>
            {notes.length
              ? `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`
              : 'Add a note'}
            <ChevronRight size={14} aria-hidden="true" />
          </span>
        </div>
      )}
      {notes.length ? null : <p className={styles.summaryEmpty}>No huddle notes yet.</p>}
      <ul className={styles.summaryList}>
        {shown.map((note) => (
          <li key={note.id} className={styles.summaryNote}>
            <p className={styles.summaryText}>{note.text}</p>
            <p className={styles.summaryMeta}>
              {[
                note.author,
                formatNoteStamp(note.createdAt),
                note.visitLabel,
                NOTE_SOURCE_LABEL[note.source],
                note.editedBy &&
                  `Edited by ${note.editedBy}${note.editedAt ? ` · ${formatNoteStamp(note.editedAt)}` : ''}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </li>
        ))}
      </ul>
      {more > 0 ? <p className={styles.summaryMore}>+{more} more</p> : null}
    </button>
  );
};

HuddleNotesSummary.propTypes = {
  onOpen: PropTypes.func,
  heading: PropTypes.bool,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string,
      author: PropTypes.string,
      createdAt: PropTypes.string,
      source: PropTypes.string,
      visitLabel: PropTypes.string,
      editedBy: PropTypes.string,
      editedAt: PropTypes.string,
    }),
  ),
};

export const HuddleNotesPanel = ({
  backLabel,
  title,
  status,
  notes = [],
  composerVisitLabel,
  onBack,
  onPostNote,
  onEditNote,
}) => (
  <>
    <BackBar
      label={backLabel}
      title={title}
      onBack={onBack}
      badge={
        READINESS_BADGE_META_BY_STATUS[status] ? (
          <Badge
            variant={READINESS_BADGE_META_BY_STATUS[status].variant}
            className={styles.panelBadge}
          >
            {READINESS_BADGE_META_BY_STATUS[status].label}
          </Badge>
        ) : null
      }
    />
    <BriefRow
      label="Huddle notes"
      meta={`${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
      flush
    >
      <HuddleNotes
        notes={notes}
        onPostNote={onPostNote}
        onEditNote={onEditNote}
        variant="panel"
        subtitle="From the morning huddle. Anyone on the team can add or edit."
        autoFocusComposer={notes.length === 0}
        composerVisitLabel={composerVisitLabel}
      />
    </BriefRow>
  </>
);

HuddleNotesPanel.propTypes = {
  backLabel: PropTypes.string.isRequired,
  title: PropTypes.string,
  status: PropTypes.string,
  notes: PropTypes.array,
  composerVisitLabel: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onPostNote: PropTypes.func.isRequired,
  onEditNote: PropTypes.func,
};
